import * as atlas from 'azure-maps-control'
import mapMarkers from '@/images/markers/markers.js'
import i18n from '@/i18n'
import store from '@/store'
import { TractStatus, TractTypeCategory, TractHudSection } from '@/utils/Enumerations.js'
import { numberWithCommas } from '@/utils/NumericMutations'
import { utcToLocalDate } from '@/utils/DateFormatter.js'

export function harvestedProgress (tract) {
  const { harvestedTons: harvested, expectedTons: expected } = tract
  if (expected <= 0 || harvested <= 0) return 0
  return Math.min(1, parseFloat((harvested / expected).toFixed(2)))
}

const ACTIVE_COLORS = { fill: '#44e94b', outline: '#0d7011' }
const DELIVERED_COLORS = { fill: '#FFFFFF', outline: '#808080' }

export const TRACT_STATUS_COLORS = {
  [TractStatus.Initiated.value]: { fill: '#B388FF', outline: '#311B92' },
  [TractStatus.Cruised.value]: { fill: '#FFB74D', outline: '#7A3E00' },
  [TractStatus.Executed.value]: { fill: '#4DD0E1', outline: '#004D57' },
  [TractStatus.Closed.value]: { fill: '#E35D5D', outline: '#5C0A0A' },
  [TractStatus.Archived.value]: { fill: '#BDBDBD', outline: '#424242' }
}

export function tractMarkerSvg (tract, progress) {
  const isDelivered = tract.type.category === TractTypeCategory.Delivered.value
  const { fill, outline } = TRACT_STATUS_COLORS[tract.status] ?? (isDelivered ? DELIVERED_COLORS : ACTIVE_COLORS)
  if (progress <= 0.05) return mapMarkers.empty(fill, outline)
  if (progress <= 0.35) return mapMarkers.twentyFive(fill, outline)
  if (progress < 0.75) return mapMarkers.fifty(fill, outline)
  if (progress <= 0.89) return mapMarkers.seventyFive(fill, outline)
  return mapMarkers.oneHundred(fill, outline)
}

function harvestDateRange (tract) {
  return [tract.harvestStartDate, tract.harvestEndDate]
    .filter(Boolean)
    .map(d => utcToLocalDate(d, 'MM/YY'))
    .join(' - ')
}

function harvestProgressTitle (tract, progress) {
  if (tract.expectedTons <= 0) return TractHudSection.HarvestProgress.name
  return i18n.t('tractHudProgressTooltip', {
    label: TractHudSection.HarvestProgress.name,
    harvested: numberWithCommas(tract.harvestedTons, 3),
    expected: numberWithCommas(tract.expectedTons, 3),
    percent: Math.round(progress * 100)
  })
}

function hudRow (section, contentHtml, icon = section.icon) {
  return `<div class="map-marker-popup-line map-marker-popup-row" title="${section.name}"><span class="mdi ${icon} map-marker-popup-row-icon"></span>${contentHtml}</div>`
}

const hudSectionHtml = {
  [TractHudSection.TractType.value]: tract => hudRow(TractHudSection.TractType, `<span>${tract.type.name}</span>`),
  [TractHudSection.TractTypeCategory.value]: tract => hudRow(TractHudSection.TractTypeCategory, `<span>${TractTypeCategory.fromInt(tract.type.category)}</span>`, TractTypeCategory.forInt(tract.type.category)?.icon),
  [TractHudSection.LoggingAccessibility.value]: tract => tract.loggingAccessibilityTag
    ? hudRow(TractHudSection.LoggingAccessibility, `<span class="map-marker-popup-access">${tract.loggingAccessibilityTag}</span>`)
    : '',
  [TractHudSection.Rainfall.value]: tract => hudRow(TractHudSection.Rainfall, `<span>${tract.rainfallDataUpdatedOn ? i18n.t('24HPrecipitationInches', { rainfall: tract.singleDayRainfallInches }) : i18n.t('precipitationDataUnavailable')}</span>`),
  [TractHudSection.HarvestDates.value]: tract => {
    const harvestDates = harvestDateRange(tract)
    return harvestDates ? hudRow(TractHudSection.HarvestDates, `<span>${harvestDates}</span>`) : ''
  },
  [TractHudSection.HarvestProgress.value]: (tract, progress) => `
      <div class="map-marker-popup-progress" title="${harvestProgressTitle(tract, progress)}">
        <div class="map-marker-popup-progress-fill" style="width: ${progress * 100}%;"></div>
        <span class="map-marker-popup-progress-label">${numberWithCommas(tract.harvestedTons, 3)} t</span>
      </div>`
}

function visibleTractHudSections (sections) {
  const configured = sections ?? store.getters['tract-hud/allTractHudSections']
  const layout = configured?.length ? configured : TractHudSection.defaultLayout()
  return layout.filter(s => s.isVisible && hudSectionHtml[s.section]).map(s => s.section)
}

function tractHudHtml (tract, progress, { detailLink = false, sections = undefined } = {}) {
  const detailLinkHtml = detailLink
    ? `<span class="mdi mdi-crosshairs-gps map-marker-popup-link" title="${i18n.t('tractDetail')}"></span>`
    : ''
  const status = TractStatus.forInt(tract.status)
  const statusIconHtml = status
    ? `<span class="mdi ${status.details.icon}" style="color: ${(TRACT_STATUS_COLORS[status.value] ?? ACTIVE_COLORS).fill}; margin-right: 4px;" title="${status.name}"></span>`
    : ''
  const sectionsHtml = visibleTractHudSections(sections)
    .map(section => hudSectionHtml[section](tract, progress))
    .join('')
  return `
      <div style="display: flex; justify-content: space-between; align-items: flex-start;">
        <p id="map-marker-popup-name">${statusIconHtml}${tract.name}</p>
        ${detailLinkHtml}
      </div>
      <hr class="map-marker-popup-divider">
      ${sectionsHtml}
      <div class="tract-popup-tail"></div>
`
}

export function tractHudElement (tract, progress, { onDetail = undefined, detailLink = !!onDetail, sections = undefined } = {}) {
  const content = document.createElement('div')
  content.className = 'tract-hud'
  content.innerHTML = tractHudHtml(tract, progress, { detailLink, sections })
  if (onDetail) content.querySelector('.map-marker-popup-link').addEventListener('click', onDetail)
  return content
}

export function tractPopup (tract, progress, focused = false, onDetail = undefined) {
  const content = tractHudElement(tract, progress, { onDetail })

  return new atlas.Popup({
    content,
    pixelOffset: [0, focused ? -45 : -35],
    closeButton: false,
    showPointer: true
  })
}

export const MAX_VIEWPORT_MILES_FOR_TRACT_MARKERS = 120

// Measured from the centre: a west-to-east distance wraps toward zero once the view spans the globe.
export function viewportWidthMiles (map) {
  const { bounds, center } = map.getCamera()
  return 2 * atlas.math.getDistanceTo(center, [bounds[2], center[1]], 'miles')
}
